import React, { useState } from "react";
import { motion } from "framer-motion";
import { GraduationCap, BookOpen, Briefcase, Lightbulb, ArrowRight } from "lucide-react";

const CALENDLY_URL = "https://cal.com/mindways/discovery-call";

const tracks = [
  {
    icon: Lightbulb,
    tag: "Early Starters — Grade 9 to 12 & Early UG",
    title: "Mentorship",
    description: "For students who want to build a strong foundation before the application process begins.",
    bullets: [
      "Profile building, projects, and internship strategy",
      "Readiness assessment and gap planning",
      "Test planning and preparation discipline",
      "Application coaching and deadline management",
    ],
    color: "#FDF4FF",
    iconColor: "#9333EA",
    border: "#9333EA",
  },
  {
    icon: GraduationCap,
    tag: "Undergraduate",
    title: "Bachelors",
    description: "School to world-class university — strategic, not rushed.",
    bullets: [
      "Strategic profile building",
      "Subject and pathway clarity",
      "Activities, essays, and LOR planning",
      "Parent guidance and timeline discipline",
    ],
    color: "#EEF0FC",
    iconColor: "#2B2F9E",
    border: "#2B2F9E",
  },
  {
    icon: BookOpen,
    tag: "Postgraduate",
    title: "Masters",
    description: "From degree to the right specialisation, globally placed.",
    bullets: [
      "Specialisation and employability alignment",
      "Research fit and course modules evaluation",
      "SOP built on projects and outcomes",
      "CV and LOR from professors or managers",
    ],
    color: "#FFF7E6",
    iconColor: "#F5A000",
    border: "#F5A000",
  },
  {
    icon: Briefcase,
    tag: "Business School",
    title: "MBA",
    description: "Career narrative, leadership proof, and school fit done right.",
    bullets: [
      "Career narrative and leadership proof",
      "CV impact, essays, and interview readiness",
      "GMAT plan and school fit strategy",
      "Strategy on timing and applications mix",
    ],
    color: "#F0FDF4",
    iconColor: "#10B981",
    border: "#10B981",
  },
];

export default function Tracks() {
  const [selected, setSelected] = useState(null);

  return (
    <section
      id="tracks"
      data-testid="tracks-section"
      style={{ padding: "100px 0", backgroundColor: "#F8FAFC" }}
    >
      <div style={{ maxWidth: "1280px", margin: "0 auto", padding: "0 24px" }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: "center", marginBottom: "60px" }}
        >
          <span style={{ fontSize: "13px", fontWeight: "700", letterSpacing: "0.12em", textTransform: "uppercase", color: "#F5A000", fontFamily: "'DM Sans', sans-serif" }}>
            Your Journey
          </span>
          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(26px, 2.5vw, 36px)",
            fontWeight: "700",
            color: "#0F172A",
            margin: "12px 0 16px",
            lineHeight: "1.25",
          }}>
            Choose your track
          </h2>
          <p style={{ fontSize: "17px", color: "#64748B", fontFamily: "'DM Sans', sans-serif", maxWidth: "440px", margin: "0 auto", lineHeight: "1.65" }}>
            Each track is built around the unique demands of that level of study.
          </p>
        </motion.div>

        {/* Track cards */}
        <div className="mw-grid-4" style={{ gap: "20px" }}>
          {tracks.map((track, i) => {
            const Icon = track.icon;
            const isSelected = selected === i;
            return (
              <motion.div
                key={track.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`track-card-${track.title.toLowerCase()}`}
                onClick={() => setSelected(isSelected ? null : i)}
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "14px",
                  padding: "32px 28px",
                  border: `2px solid ${isSelected ? track.border : "#E2E8F0"}`,
                  cursor: "pointer",
                  transition: "all 0.3s",
                  boxShadow: isSelected ? `0 12px 32px ${track.border}22` : "0 2px 8px rgba(0,0,0,0.04)",
                }}
                whileHover={{ y: -4 }}
              >
                <span style={{ fontSize: "11px", fontWeight: "700", letterSpacing: "0.1em", textTransform: "uppercase", color: track.iconColor, fontFamily: "'DM Sans', sans-serif", lineHeight: "1.3", display: "block", marginBottom: "14px" }}>
                  {track.tag}
                </span>

                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "14px" }}>
                  <div style={{ width: "48px", height: "48px", borderRadius: "10px", backgroundColor: track.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Icon size={22} style={{ color: track.iconColor }} />
                  </div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "26px", fontWeight: "700", color: "#0F172A", margin: 0 }}>
                    {track.title}
                  </h3>
                </div>

                <p style={{ fontSize: "14px", color: "#64748B", fontFamily: "'DM Sans', sans-serif", margin: "0 0 20px", lineHeight: "1.6" }}>
                  {track.description}
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {track.bullets.map((b) => (
                    <div key={b} style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                      <div style={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: track.iconColor, marginTop: "7px", flexShrink: 0 }} />
                      <span style={{ fontSize: "13px", color: "#475569", fontFamily: "'DM Sans', sans-serif", lineHeight: "1.5" }}>{b}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: "center", marginTop: "48px" }}
        >
          <button
            data-testid="tracks-get-roadmap-btn"
            onClick={() => window.open(CALENDLY_URL, "_blank")}
            style={{
              backgroundColor: "#F5A000",
              color: "#fff",
              border: "none",
              padding: "16px 36px",
              borderRadius: "6px",
              fontSize: "16px",
              fontWeight: "700",
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              boxShadow: "0 8px 24px rgba(245,160,0,0.3)",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
          >
            Get a personalised roadmap <ArrowRight size={16} />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
